import React, { useState, useEffect } from "react";
import {
	Box,
	FormControl,
	FormHelperText,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
	Controller,
	type Control,
	type FieldErrors,
	type UseFormRegister,
} from "react-hook-form";

import type { PriceType } from "../type/offlineExhibitorType";
export type { PriceType };

interface FormValues {
	name: string;
	map: string;
	stalls: {
		hall_name: string;
		stall_no: string;
	}[];
	services: {
		service_name: string;
		price_type: PriceType;
		price: string;
		service_description: string;
	}[];
}

interface Props {
	fieldId?: string;
	index: number;
	control: Control<any>;
	register: UseFormRegister<any>;
	errors: FieldErrors<any>;
	removeService: (index: number) => void;
	canDelete: boolean;
}

const parseInitialPrice = (val: string | undefined, defaultType?: PriceType) => {
	const raw = (val || "").trim();
	if (defaultType) {
		if (defaultType === "range" || raw.includes("-")) {
			const parts = raw.split("-");
			return {
				type: "range" as PriceType,
				fixed: "",
				min: parts[0]?.trim() || "",
				max: parts[1]?.trim() || "",
			};
		}
		if (defaultType === "negotiable" || raw.toLowerCase() === "negotiable") {
			return {
				type: "negotiable" as PriceType,
				fixed: raw || "Negotiable",
				min: "",
				max: "",
			};
		}
		return {
			type: "fixed" as PriceType,
			fixed: raw,
			min: "",
			max: "",
		};
	}
	if (raw.includes("-")) {
		const parts = raw.split("-");
		return {
			type: "range" as PriceType,
			fixed: "",
			min: parts[0]?.trim() || "",
			max: parts[1]?.trim() || "",
		};
	}
	if (raw.toLowerCase() === "negotiable") {
		return {
			type: "negotiable" as PriceType,
			fixed: raw,
			min: "",
			max: "",
		};
	}
	return {
		type: "fixed" as PriceType,
		fixed: raw,
		min: "",
		max: "",
	};
};

const ServiceItemRow: React.FC<Props> = ({
	fieldId,
	index,
	control,
	register,
	errors,
	removeService,
	canDelete,
}) => {
	return (
		<Paper
			variant="outlined"
			sx={{ p: 2, mb: 1.5, borderRadius: 2, bgcolor: "background.paper" }}
		>
			<Controller
				control={control}
				name={`services.${index}.price_type` as const}
				defaultValue="fixed"
				render={({ field: { value: formPriceType, onChange: onPriceTypeChange } }) => (
					<Controller
						control={control}
						name={`services.${index}.price` as const}
						rules={{
							validate: (val, formValues: any) => {
								const currentService = formValues?.services?.[index];
								const hasAnyOther =
									Boolean(currentService?.service_name?.trim()) ||
									Boolean(currentService?.service_description?.trim());
								if (!hasAnyOther && (!val || val.trim() === "")) return true;
								if (hasAnyOther && (!val || val.trim() === "")) return "Price is required";
								return true;
							},
						}}
						render={({
							field: { value: formPriceValue, onChange: onPriceChange },
							fieldState: { error: priceError },
						}) => {
							return (
								<ServiceItemFields
									fieldId={fieldId}
									index={index}
									register={register}
									errors={errors}
									removeService={removeService}
									canDelete={canDelete}
									formPriceType={formPriceType || "fixed"}
									onPriceTypeChange={onPriceTypeChange}
									formPriceValue={formPriceValue || ""}
									onPriceChange={onPriceChange}
									priceError={priceError}
								/>
							);
						}}
					/>
				)}
			/>
		</Paper>
	);
};

interface FieldsProps {
	fieldId?: string;
	index: number;
	register: UseFormRegister<FormValues>;
	errors: FieldErrors<FormValues>;
	removeService: (index: number) => void;
	canDelete: boolean;
	formPriceType: PriceType;
	onPriceTypeChange: (val: PriceType) => void;
	formPriceValue: string;
	onPriceChange: (val: string) => void;
	priceError?: { message?: string };
}

const ServiceItemFields: React.FC<FieldsProps> = ({
	fieldId,
	index,
	register,
	errors,
	removeService,
	canDelete,
	formPriceType,
	onPriceTypeChange,
	formPriceValue,
	onPriceChange,
	priceError,
}) => {
	const initial = parseInitialPrice(formPriceValue, formPriceType);
	const [priceType, setPriceType] = useState<PriceType>(formPriceType || initial.type);
	const [fixedVal, setFixedVal] = useState<string>(initial.fixed);
	const [minVal, setMinVal] = useState<string>(initial.min);
	const [maxVal, setMaxVal] = useState<string>(initial.max);
	const lastPropagatedValue = React.useRef<string>(formPriceValue);

	// Sync when formPriceValue or formPriceType changes externally (e.g. form reset or prefill)
	useEffect(() => {
		if (
			formPriceValue !== lastPropagatedValue.current ||
			(formPriceType && formPriceType !== priceType)
		) {
			lastPropagatedValue.current = formPriceValue;
			const parsed = parseInitialPrice(formPriceValue, formPriceType);
			setPriceType(formPriceType || parsed.type);
			setFixedVal(parsed.fixed);
			setMinVal(parsed.min);
			setMaxVal(parsed.max);
		}
	}, [formPriceValue, formPriceType]);

	const handlePriceTypeChange = (newType: PriceType) => {
		setPriceType(newType);
		onPriceTypeChange(newType);
		if (newType === "fixed") {
			const nextFixed = fixedVal.toLowerCase() === "negotiable" ? "" : fixedVal;
			setFixedVal(nextFixed);
			lastPropagatedValue.current = nextFixed;
			onPriceChange(nextFixed);
		} else if (newType === "negotiable") {
			const nextVal = "Negotiable";
			setFixedVal(nextVal);
			lastPropagatedValue.current = nextVal;
			onPriceChange(nextVal);
		} else if (newType === "range") {
			const currentMin = minVal || (fixedVal && fixedVal.toLowerCase() !== "negotiable" ? fixedVal : "");
			if (!minVal && currentMin) setMinVal(currentMin);
			const combined = currentMin || maxVal ? `${currentMin}-${maxVal}` : "";
			lastPropagatedValue.current = combined;
			onPriceChange(combined);
		}
	};

	const handleFixedChange = (val: string) => {
		setFixedVal(val);
		lastPropagatedValue.current = val;
		onPriceChange(val);
	};

	const handleMinChange = (val: string) => {
		setMinVal(val);
		const combined = `${val}-${maxVal}`;
		lastPropagatedValue.current = combined;
		onPriceChange(combined);
	};

	const handleMaxChange = (val: string) => {
		setMaxVal(val);
		const combined = `${minVal}-${val}`;
		lastPropagatedValue.current = combined;
		onPriceChange(combined);
	};

	const rowLabelId = fieldId ? `price-type-label-${fieldId}` : `price-type-label-${index}`;

	return (
		<Grid container spacing={2} alignItems="flex-start">
			{/* Service Name */}
			<Grid item key="service-name-col" xs={12} sm={4.5}>
				<TextField
					label={`Service Name - ${index + 1}`}
					fullWidth
					size="small"
					{...register(`services.${index}.service_name` as const, {
						validate: (val, formValues: any) => {
							const currentService = formValues?.services?.[index];
							const hasAnyOther =
								Boolean(currentService?.price?.trim()) ||
								Boolean(currentService?.service_description?.trim());
							if (!hasAnyOther && (!val || val.trim() === "")) return true;
							if (hasAnyOther && (!val || val.trim() === "")) return "Service name is required";
							return true;
						},
					})}
					error={!!errors.services?.[index]?.service_name}
					helperText={errors.services?.[index]?.service_name?.message}
					placeholder="e.g. Visa Processing Helper"
				/>
			</Grid>

			{/* Price Type Dropdown */}
			<Grid item key="price-type-col" xs={12} sm={2.5}>
				<FormControl fullWidth size="small">
					<InputLabel id={rowLabelId}>Price Type</InputLabel>
					<Select
						labelId={rowLabelId}
						label="Price Type"
						value={priceType}
						onChange={(e) => handlePriceTypeChange(e.target.value as PriceType)}
					>
						<MenuItem key="fixed" value="fixed">Fixed</MenuItem>
						<MenuItem key="negotiable" value="negotiable">Negotiable</MenuItem>
						<MenuItem key="range" value="range">Range</MenuItem>
					</Select>
				</FormControl>
			</Grid>

			{/* Price Input(s) */}
			<Grid item key="price-inputs-col" xs={10} sm={4.5}>
				{priceType === "range" ? (
					<Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<TextField
								label="Min Price (BDT)"
								fullWidth
								size="small"
								type="number"
								value={minVal}
								onChange={(e) => handleMinChange(e.target.value)}
								placeholder="e.g. 20000"
								error={!!priceError && !minVal && !maxVal}
							/>
							<Typography variant="body1" fontWeight="bold" color="text.secondary">
								-
							</Typography>
							<TextField
								label="Max Price (BDT)"
								fullWidth
								size="small"
								type="number"
								value={maxVal}
								onChange={(e) => handleMaxChange(e.target.value)}
								placeholder="e.g. 5000"
								error={!!priceError && !minVal && !maxVal}
							/>
						</Box>
						{priceError && !minVal && !maxVal && (
							<FormHelperText error sx={{ ml: 1, mt: 0.5 }}>
								{priceError.message || "Price range is required"}
							</FormHelperText>
						)}
					</Box>
				) : priceType === "negotiable" ? (
					<TextField
						label={`Price / Note - ${index + 1}`}
						fullWidth
						size="small"
						value={fixedVal}
						onChange={(e) => handleFixedChange(e.target.value)}
						placeholder="e.g. Negotiable"
						error={!!priceError}
						helperText={priceError?.message}
					/>
				) : (
					<TextField
						label={`Price (BDT) - ${index + 1}`}
						fullWidth
						size="small"
						type="number"
						value={fixedVal}
						onChange={(e) => handleFixedChange(e.target.value)}
						placeholder="e.g. 5000.00"
						error={!!priceError}
						helperText={priceError?.message}
					/>
				)}
			</Grid>

			{/* Remove Button */}
			<Grid item key="service-remove-col" xs={2} sm={0.5} textAlign="center" sx={{ pt: { xs: 0.5, sm: 0.5 } }}>
				<IconButton
					color="error"
					size="small"
					onClick={() => removeService(index)}
					disabled={!canDelete}
				>
					<DeleteOutlineIcon fontSize="small" />
				</IconButton>
			</Grid>

			{/* Service Description */}
			<Grid item key="service-desc-col" xs={12}>
				<TextField
					label={`Service Description - ${index + 1}`}
					fullWidth
					size="small"
					multiline
					rows={2}
					{...register(`services.${index}.service_description` as const)}
					placeholder="e.g. Express Schengen & UK visa consultancy"
				/>
			</Grid>
		</Grid>
	);
};

export default ServiceItemRow;
